import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Eager: landing page (fastest first paint) + always-visible overlays
import HomePage from "@/pages/HomePage";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { CompareBar } from "@/components/CompareBar";
import { CompareModal } from "@/components/CompareModal";
import { CookieBanner } from "@/components/CookieBanner";

// Context
import { CompareProvider } from "@/context/CompareContext";

// Lazy: split every route below the landing page into its own chunk
const CarsPage = lazy(() => import("@/pages/CarsPage"));
const CarDetailsPage = lazy(() => import("@/pages/CarDetailsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));

// Lazy: entire admin area (kept out of the public bundle)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminCars = lazy(() => import("@/pages/admin/AdminCars"));
const AdminCarForm = lazy(() => import("@/pages/admin/AdminCarForm"));
const AdminStats = lazy(() => import("@/pages/admin/AdminStats"));
const AdminPublish = lazy(() => import("@/pages/admin/AdminPublish"));

// Minimal fallback matching the app background (no white flash, no visual change)
const RouteFallback = () => <div className="min-h-screen bg-[#0a0a0a]" />;

function App() {
  return (
    <div className="App">
      <CompareProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/masini" element={<CarsPage />} />
              <Route path="/masini/:id" element={<CarDetailsPage />} />
              <Route path="/politica-confidentialitate" element={<PrivacyPolicyPage />} />
              <Route path="/termeni-conditii" element={<TermsPage />} />
              <Route path="/politica-cookies" element={<CookiePolicyPage />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="masini" element={<AdminCars />} />
                <Route path="masini/adauga" element={<AdminCarForm />} />
                <Route path="masini/editeaza/:id" element={<AdminCarForm />} />
                <Route path="statistici" element={<AdminStats />} />
                <Route path="publicare" element={<AdminPublish />} />
              </Route>
            </Routes>
          </Suspense>

          {/* WhatsApp Float - only on public pages */}
          <WhatsAppFloat />

          {/* Compare System */}
          <CompareBar />
          <CompareModal />

          {/* Cookie Banner */}
          <CookieBanner />

          <Toaster position="top-right" />
        </BrowserRouter>
      </CompareProvider>
    </div>
  );
}

export default App;
